/**
 * @file The ledger — entry vocabulary, the sign convention, and the balance rule
 * (T1.3).
 *
 * Four things live here, and they belong together because they are the same
 * decision seen from four angles:
 *
 *   1. **The entry types and payment states as data.** PLAN.md §5 lists them and
 *      `packages/db/migrations/0006_ledger.sql` declares them as Postgres enums;
 *      this file is where the API, the SPA and the tests read the same words and
 *      the same human labels, so a badge can never render `late_fee` raw.
 *   2. **The sign convention** (`LEDGER_ENTRY_SIGNS`). `amount_cents` is signed
 *      and the sign is the effect on what the TENANT owes, which is what makes a
 *      balance a plain integer sum instead of a CASE over entry_type. The SQL
 *      migration records the same reasoning; this is its executable half.
 *   3. **The balance rule** (`countedLedgerEntries`) — the one place that decides
 *      which rows a balance is allowed to count, including the correction and
 *      supersession interaction. Nothing else may filter ledger rows for money.
 *   4. **The schemas and DTOs** for the two write routes and the three reads.
 *
 * Money is INTEGER CENTS everywhere (CLAUDE.md / INV-MONEY-1) and never a float:
 * `netBalanceCents` refuses a non-integer amount rather than rounding it, because
 * a float that has survived this far has already lost precision somewhere.
 *
 * The kernel discipline applies as everywhere in `packages/shared`: no drizzle,
 * no app code. The SPA imports these TYPES and, for labels and the balance rule,
 * these values — the API remains the authority on what may be written.
 */
import { z } from "zod";
import { requiredPanFree } from "./pan.js";

/**
 * The ledger entry types, exactly the nine PLAN.md §5 names.
 *
 * Mirrored by the `ledger_entry_type` enum in migration 0006. Several are
 * RESERVED and written by nothing yet: `late_fee` (T1.4), `payment`/`refund`
 * (T1.5), `deposit_return` (T1.9) and `expense` (T1.10). The list is complete
 * now because the enum is a database shape, and extending it later would be a
 * migration to do what this file already says.
 */
export const LEDGER_ENTRY_TYPES = [
  "rent_charge",
  "late_fee",
  "deposit_charge",
  "deposit_return",
  "payment",
  "refund",
  "writeoff",
  "adjustment",
  "expense",
] as const;

export type LedgerEntryType = (typeof LEDGER_ENTRY_TYPES)[number];

/** Mutable-tuple form, for `z.enum` (mirrors LEASE_STATUS_VALUES). */
export const LEDGER_ENTRY_TYPE_VALUES = LEDGER_ENTRY_TYPES.map(
  (entryType) => entryType,
) as [LedgerEntryType, ...LedgerEntryType[]];

/**
 * Human labels for the same nine types.
 *
 * Here rather than in the SPA because the ledger screen and the future lease
 * detail both render them, and `deposit_return` appearing raw in a money column
 * is how two screens start disagreeing about what an entry is.
 */
export const LEDGER_ENTRY_TYPE_LABELS: Readonly<Record<LedgerEntryType, string>> = {
  rent_charge: "Rent charge",
  late_fee: "Late fee",
  deposit_charge: "Deposit charge",
  deposit_return: "Deposit return",
  payment: "Payment",
  refund: "Refund",
  writeoff: "Write-off",
  adjustment: "Adjustment",
  expense: "Expense",
};

/**
 * The sign each entry type carries in the balance (PLAN.md §7.2, migration 0006
 * header).
 *
 * `+1` increases what the tenant owes, `-1` reduces it, and `0` means the
 * DIRECTION IS THE CONTENT — an `adjustment` is a landlord's correction in either
 * direction, and an `expense` is a landlord cost rather than a tenant obligation.
 *
 * A record rather than a chain of `if`s for the same reason `LEASE_TRANSITIONS`
 * is one: it is total over the type union, so adding an entry type without saying
 * which way it points fails to compile instead of quietly defaulting to positive.
 */
export const LEDGER_ENTRY_SIGNS: Readonly<Record<LedgerEntryType, 1 | -1 | 0>> = {
  rent_charge: 1,
  late_fee: 1,
  deposit_charge: 1,
  deposit_return: -1,
  // A payment reduces what is owed; a refund gives money back, which puts the
  // tenant back in arrears. Same wallet, opposite signs — the reason
  // `amount_cents` is signed rather than a magnitude.
  payment: -1,
  refund: 1,
  writeoff: -1,
  adjustment: 0,
  expense: 0,
};

/** Which way this entry type points, or 0 when the writer states the direction. */
export function ledgerSign(entryType: LedgerEntryType): 1 | -1 | 0 {
  return LEDGER_ENTRY_SIGNS[entryType];
}

/**
 * Is this amount consistent with its type's sign?
 *
 * Used by the write schemas below, so the exact bug this file warns about — a
 * `payment` recorded as `+185000`, which would be read back as money still owed —
 * is rejected at the boundary rather than discovered in a balance weeks later.
 * Types whose sign is the content (`adjustment`, `expense`) always pass.
 */
export function ledgerAmountAgreesWithSign(
  entryType: LedgerEntryType,
  amountCents: number,
): boolean {
  // Zero is never an amount, whatever the type: a ledger row that records no
  // money is not a ledger row (the CHECK in migration 0006 says the same).
  if (amountCents === 0) return false;
  const expected = LEDGER_ENTRY_SIGNS[entryType];
  if (expected === 0) return true;
  return Math.sign(amountCents) === expected;
}

/**
 * Does this entry type move the TENANT's balance?
 *
 * Everything except `expense`. An expense is a landlord's cost that PLAN.md §5
 * also gives its own table (`expense_entries`, T1.10); when it appears in the
 * ledger it is there to make a property's income and outgoings visible in one
 * place (T2.11's Schedule-E export), and summing it into what a tenant owes would
 * say that fixing a boiler reduces the rent.
 */
export function affectsTenantBalance(entryType: LedgerEntryType): boolean {
  return entryType !== "expense";
}

/** The payment lifecycle values PLAN.md §5 names. Mirrored by migration 0006. */
export const LEDGER_PAYMENT_STATES = ["n/a", "pending", "settled", "returned"] as const;
export type LedgerPaymentState = (typeof LEDGER_PAYMENT_STATES)[number];
export const LEDGER_PAYMENT_STATE_VALUES = LEDGER_PAYMENT_STATES.map(
  (state) => state,
) as [LedgerPaymentState, ...LedgerPaymentState[]];

/** `n/a` is not "unknown": it is the ordinary state of an obligation. */
export const LEDGER_PAYMENT_STATE_LABELS: Readonly<Record<LedgerPaymentState, string>> = {
  "n/a": "Not applicable",
  pending: "Pending",
  settled: "Settled",
  returned: "Returned",
};

/** How money moved. Mirrored by migration 0006. NULL when nothing moved. */
export const LEDGER_CHANNELS = ["online", "cash", "check", "other"] as const;
export type LedgerChannel = (typeof LEDGER_CHANNELS)[number];
export const LEDGER_CHANNEL_VALUES = LEDGER_CHANNELS.map(
  (channel) => channel,
) as [LedgerChannel, ...LedgerChannel[]];

export const LEDGER_CHANNEL_LABELS: Readonly<Record<LedgerChannel, string>> = {
  online: "Online",
  cash: "Cash",
  check: "Check",
  other: "Other",
};

// ---------------------------------------------------------------------------
// The balance rule
// ---------------------------------------------------------------------------

/**
 * The fields the balance rule reads.
 *
 * Structural rather than a class, so the API's drizzle rows, the SPA's wire type
 * and a test literal all satisfy it without a conversion.
 */
export interface LedgerEntryLike {
  readonly id: string;
  readonly entryType: LedgerEntryType;
  /** Signed integer cents. Positive = the tenant owes more. */
  readonly amountCents: number;
  readonly leaseId: string;
  readonly leaseTenantId: string | null;
  readonly correctionOf: string | null;
  readonly superseded: boolean;
}

/**
 * **The correction-pointer test, in one place.** A correction is a new row that
 * names the row it replaces; `correction_of` is that name.
 *
 * Exported because the correction route, the audit view and the tests all need
 * to ask the same question, and `entry.correctionOf !== null` written three times
 * is how one of them ends up asking it backwards.
 */
export function isCorrection(entry: Pick<LedgerEntryLike, "correctionOf">): boolean {
  return entry.correctionOf !== null;
}

/** Has this row been replaced? The flag the correction wrote, in one place. */
export function isLiveEntry(entry: Pick<LedgerEntryLike, "superseded">): boolean {
  return !entry.superseded;
}

/**
 * **THE BALANCE RULE.** Every money figure in this product is computed over the
 * rows this function returns, and nowhere else filters ledger rows for money.
 *
 * A row COUNTS iff it is live **and** no live correction points at it, directly
 * or through a chain of corrections:
 *
 *   * `correction_of IS NULL` is the ROOT test — a root is a row that states a
 *     fact (a charge, a payment) rather than replacing one. The `correction_of`
 *     pointer is read in exactly this function, and a root stops counting the
 *     moment a live correction points at it. **A correction row counts**: it is
 *     the replacement, carrying the corrected amount, type and attribution, so
 *     counting the root as well would be counting the same money twice — at the
 *     old value, which is the worst of both.
 *   * `superseded` is the FLAG the correction route sets on the row it replaces,
 *     in the same transaction as the insert. The database's append-only trigger
 *     permits exactly that one transition and nothing else (migration 0006), so
 *     in practice the flag is always right.
 *   * The rule does not take the flag on faith, and it does not rely on the
 *     pointer being maintained either: it requires BOTH to say the row is live.
 *     A row whose flag was never set but that a live correction points at is
 *     excluded (otherwise a half-applied correction would count the stale
 *     original AND its replacement); a row whose flag WAS set counts nothing
 *     even if its correction went missing. That is the explicit interaction
 *     between correction and supersession, and it is why neither half alone is
 *     the rule — `correction_of IS NULL` alone counts the stale original and
 *     drops the replacement, and `superseded = false` alone trusts a flag.
 *   * The exclusion walks ancestors transitively, so a correction chain collapses
 *     to its tail: correcting a correction replaces the first correction too, and
 *     exactly one row per chain is ever counted. A chain is linear because the
 *     correction route refuses (409) to correct a row that is already superseded,
 *     so two live corrections can never point at the same row.
 */
export function countedLedgerEntries<T extends LedgerEntryLike>(
  entries: readonly T[],
): readonly T[] {
  const byId = new Map<string, T>();
  for (const entry of entries) byId.set(entry.id, entry);

  // Every row a live row replaces, directly or through the chain behind it.
  const replaced = new Set<string>();
  const pending: string[] = [];
  for (const entry of entries) {
    if (isLiveEntry(entry) && isCorrection(entry) && entry.correctionOf !== null) {
      pending.push(entry.correctionOf);
    }
  }
  while (pending.length > 0) {
    const id = pending.pop();
    if (id === undefined || replaced.has(id)) continue;
    replaced.add(id);
    const replacedEntry = byId.get(id);
    if (replacedEntry !== undefined && replacedEntry.correctionOf !== null) {
      pending.push(replacedEntry.correctionOf);
    }
  }

  return entries.filter((entry) => isLiveEntry(entry) && !replaced.has(entry.id));
}

/**
 * Integer-only addition. Money is INTEGER CENTS (CLAUDE.md); a fractional amount
 * is a bug that has already happened upstream, so it throws here rather than
 * being silently truncated into a report.
 */
function sumCents(values: readonly number[]): number {
  let total = 0;
  for (const value of values) {
    if (!Number.isInteger(value)) {
      throw new Error(
        `ledger: ${String(value)} is not a whole number of cents — money is INTEGER CENTS (CLAUDE.md)`,
      );
    }
    total += value;
  }
  return total;
}

/**
 * The net balance of a set of entries, in integer cents: positive = the tenant
 * owes money, negative = the tenant is in credit.
 *
 * Counted rows only (`countedLedgerEntries`), and only the types that move a
 * tenant's balance (`affectsTenantBalance`). Pass one lease's entries for the
 * joint view, or a subset for any other.
 *
 * An empty ledger sums to 0. That is arithmetic, not a claim about the world —
 * it is the SCREEN's job not to render it as "nothing is owed" when nothing has
 * been recorded at all (see `apps/web/src/components/LedgerScreen.tsx`).
 */
export function netBalanceCents(entries: readonly LedgerEntryLike[]): number {
  return sumCents(
    countedLedgerEntries(entries)
      .filter((entry) => affectsTenantBalance(entry.entryType))
      .map((entry) => entry.amountCents),
  );
}

/** One bucket of the per-tenant view. `null` is the unattributed bucket. */
export interface LedgerAttributionBalance {
  readonly leaseTenantId: string | null;
  readonly balanceCents: number;
}

/**
 * The per-tenant view of the same rule (PLAN.md §10 T1.3: "joint + per-tenant
 * views").
 *
 * Built from the rows themselves, so every figure is checkable against the list
 * beside it, and a bucket exists only when a live entry put money in it — no
 * invented zeros for tenants who have nothing posted. `null` collects the entries
 * attributed to nobody, which on a joint lease is the ordinary case: the
 * household owes the rent, and picking a person would be an invention.
 *
 * Ordered by attribution id, with the unattributed bucket last. That order is
 * DETERMINISTIC, not meaningful — the ids are random uuids — and it exists so two
 * calls over the same rows agree. A caller that shows names orders them itself
 * (apps/api/src/routes/ledger.ts sorts the buckets by name for exactly that
 * reason).
 */
export function perTenantBalances(
  entries: readonly LedgerEntryLike[],
): readonly LedgerAttributionBalance[] {
  const counted = countedLedgerEntries(entries).filter((entry) =>
    affectsTenantBalance(entry.entryType),
  );

  const byTenant = new Map<string, number[]>();
  for (const entry of counted) {
    const key = entry.leaseTenantId ?? "";
    const bucket = byTenant.get(key);
    if (bucket === undefined) byTenant.set(key, [entry.amountCents]);
    else bucket.push(entry.amountCents);
  }

  return [...byTenant.entries()]
    .map(([key, amounts]) => ({
      leaseTenantId: key.length === 0 ? null : key,
      balanceCents: sumCents(amounts),
    }))
    .sort((a, b) => {
      if (a.leaseTenantId === null) return 1;
      if (b.leaseTenantId === null) return -1;
      return a.leaseTenantId < b.leaseTenantId ? -1 : 1;
    });
}

// ---------------------------------------------------------------------------
// Input schemas
// ---------------------------------------------------------------------------

/**
 * Optional text stored as `null` when absent or blank — the same normalisation
 * `properties.ts` and `leases.ts` use, and for the same reason: a cleared field
 * must not persist as `""`, or "no memo" gets two representations.
 */
function optionalText(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((value) =>
      value === undefined || value === null || value.length === 0 ? null : value,
    );
}

/**
 * Money in integer cents. SIGNED, and never zero.
 *
 * No `.min(0)`: a negative amount is how a payment, a write-off or a deposit
 * return is recorded (migration 0006's header explains the rule). Zero is refused
 * because a ledger row that records no money is not a ledger row — a memo without
 * an amount belongs in a `memo`, not in the money column.
 */
const signedMoneyCents = z
  .number()
  .int("money must be whole cents — no decimals")
  .refine((value) => value !== 0, "a ledger entry records an amount; zero is not an entry");

/**
 * The fields a new entry carries.
 *
 * NOT accepted here, each for a reason:
 *   - `correctionOf` — a correction names its predecessor in the PATH
 *     (`POST …/ledger/:entryId/correction`), so a body cannot point a correction
 *     at an entry the caller did not address. `superseded` likewise: the route
 *     sets it on the row being replaced, never on the row being written.
 *   - `leaseId` — the lease is in the path of the lease-scoped POST, and is
 *     inherited from the target for a correction. A body that could move an entry
 *     to another lease would be a way to post money against an agreement the
 *     caller never named.
 *   - the Stripe references (`stripePaymentIntent`, `stripeCharge`,
 *     `stripeBalanceTxn`) — nothing writes them yet (the payment paths are
 *     T2.1–T2.5), and a landlord-facing body that could assert a Stripe id is a
 *     way to claim a payment Stripe never made. The columns exist and stay NULL.
 */
const ledgerEntryFieldsSchema = z.object({
  /**
   * Per-tenant attribution. Optional, and NULL (absent) is the ordinary case for
   * a joint lease; the route refuses an id that is not a tenant on THIS lease.
   */
  leaseTenantId: z
    .string()
    .uuid("must be a lease tenant id")
    .nullish()
    .transform((value) => value ?? null),
  entryType: z.enum(LEDGER_ENTRY_TYPE_VALUES),
  amountCents: signedMoneyCents,
  /** The Stripe lifecycle state. `n/a` is the ordinary state of an obligation. */
  paymentState: z.enum(LEDGER_PAYMENT_STATE_VALUES).default("n/a"),
  /** How money moved; NULL when the entry records an obligation. */
  channel: z
    .enum(LEDGER_CHANNEL_VALUES)
    .nullish()
    .transform((value) => value ?? null),
  memo: optionalText(500),
});

/** Reject an amount whose sign contradicts its type (see `LEDGER_ENTRY_SIGNS`). */
function enforceSignConvention(
  value: { readonly entryType: LedgerEntryType; readonly amountCents: number },
  ctx: z.RefinementCtx,
): void {
  if (ledgerAmountAgreesWithSign(value.entryType, value.amountCents)) return;
  const expected = LEDGER_ENTRY_SIGNS[value.entryType];
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ["amountCents"],
    message:
      expected === -1
        ? `a ${value.entryType} reduces what the tenant owes, so it must be a NEGATIVE number of cents`
        : `a ${value.entryType} increases what the tenant owes, so it must be a POSITIVE number of cents`,
  });
}

/** POST /workspaces/:workspaceId/leases/:leaseId/ledger body. */
export const ledgerEntryCreateSchema =
  ledgerEntryFieldsSchema.superRefine(enforceSignConvention);

/**
 * POST /workspaces/:workspaceId/ledger/:entryId/correction body.
 *
 * The same shape as a create, because a correction is a full replacement: it
 * carries the corrected type, amount and attribution, and the row it replaces
 * keeps its original values forever (the append-only trigger guarantees that).
 *
 * `memo` is REQUIRED here and optional on a create. A correction is the only
 * operation that changes what a previous ledger row means, and the reason is the
 * one thing the new row cannot derive from the old — an unexplained correction is
 * indistinguishable from a mistake when someone reads the ledger a year later.
 * That is an auditability requirement, not a product rule about money. The memo
 * is also PAN-free: no card or bank account numbers (product law).
 */
export const ledgerCorrectionCreateSchema = ledgerEntryFieldsSchema
  .extend({ memo: requiredPanFree(500) })
  .superRefine(enforceSignConvention);

export type LedgerEntryCreateInput = z.infer<typeof ledgerEntryCreateSchema>;
export type LedgerCorrectionCreateInput = z.infer<typeof ledgerCorrectionCreateSchema>;

/** The PRE-parse shapes, for the SPA to build request bodies from. */
export type LedgerEntryCreateBody = z.input<typeof ledgerEntryCreateSchema>;
export type LedgerCorrectionCreateBody = z.input<typeof ledgerCorrectionCreateSchema>;

// ---------------------------------------------------------------------------
// DTOs — what the API returns
// ---------------------------------------------------------------------------

/**
 * One ledger entry, as the API returns it.
 *
 * Every money field is integer cents, and the SIGN is part of the meaning (see
 * `LEDGER_ENTRY_SIGNS`). The Stripe reference columns are deliberately absent:
 * nothing writes them yet, and a screen must not render a payment reference no
 * payment produced.
 */
export interface LedgerEntryDto {
  readonly id: string;
  readonly leaseId: string;
  /** NULL for an entry the household owes; set when one person is attributed. */
  readonly leaseTenantId: string | null;
  readonly entryType: LedgerEntryType;
  /** Signed integer cents: positive = the tenant owes more. */
  readonly amountCents: number;
  /** The row this one replaces, or NULL when this row states a fact. */
  readonly correctionOf: string | null;
  /** Replaced by a correction — never counted in a balance. */
  readonly superseded: boolean;
  readonly paymentState: LedgerPaymentState;
  readonly channel: LedgerChannel | null;
  readonly memo: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * A ledger entry plus the context that makes it identifiable: the lease, the
 * unit, the property and (when attributed) the person.
 *
 * The portfolio-wide ledger is read by a landlord looking at money, so each row
 * names where it came from without a request per row — the same reasoning as
 * `LeaseListRowDto`.
 */
export interface LedgerListRowDto extends LedgerEntryDto {
  readonly leaseStatus: string;
  readonly unitId: string;
  readonly unitLabel: string;
  readonly propertyId: string;
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
  /** The attributed person's name, or NULL when nobody is attributed. */
  readonly tenantName: string | null;
}

/**
 * The counts a ledger header renders, folded from the rows returned beside them.
 *
 * `entries` counts every row (including superseded ones and their corrections),
 * `live` counts the rows a balance may count, and `corrections` counts the rows
 * that replace another. They are separate because "12 entries" and "9 postings"
 * are different claims, and a single number would have to pick one silently.
 */
export interface LedgerCountsDto {
  readonly entries: number;
  readonly live: number;
  readonly corrections: number;
}

/** GET /workspaces/:workspaceId/ledger — one page of entries (?limit=, ?cursor=). Counts are workspace-wide. */
export interface LedgerListDto {
  readonly counts: LedgerCountsDto & {
    /** Distinct leases with at least one entry — not the count of entries. */
    readonly leases: number;
  };
  /**
   * The net balance of every lease with a live posting, summed by the database over
   * the WHOLE ledger (never over the rows of one page). Address, then unit order.
   */
  readonly balances: readonly LedgerLeaseBalanceDto[];
  /** True when more leases than the cap had a balance; the list above is the first of them. */
  readonly balancesTruncated?: true;
  /** One page of entries, newest first. */
  readonly entries: readonly LedgerListRowDto[];
  /** Pass as `?cursor=` for the next page; null on the last page. */
  readonly nextCursor: string | null;
}

/** One lease's net balance on the portfolio ledger. */
export interface LedgerLeaseBalanceDto {
  readonly leaseId: string;
  readonly unitId: string;
  readonly unitLabel: string;
  readonly propertyId: string;
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
  /** Signed integer cents over the counted rows. */
  readonly balanceCents: number;
}

/** One bucket of the per-tenant view, with the person's name when there is one. */
export interface LedgerAttributionBalanceDto extends LedgerAttributionBalance {
  /** The attributed person's name, or NULL for the unattributed bucket. */
  readonly fullName: string | null;
}

/**
 * GET /workspaces/:workspaceId/leases/:leaseId/ledger — one lease's ledger.
 *
 * `balanceCents` is the joint view (`netBalanceCents` over the live rows) and
 * `perTenant` is the split view (`perTenantBalances`), both computed by the ONE
 * rule in this file. `unattributedCents` is repeated from `perTenant` so a screen
 * does not have to find the NULL bucket to say what the household owes.
 */
export interface LeaseLedgerDto {
  readonly leaseId: string;
  readonly counts: LedgerCountsDto;
  /** Signed integer cents over the live entries. Empty ledger = 0. */
  readonly balanceCents: number;
  readonly unattributedCents: number;
  readonly perTenant: readonly LedgerAttributionBalanceDto[];
  /** Newest first, like the portfolio-wide list. */
  readonly entries: readonly LedgerEntryDto[];
}
